// The client portal's auth state machine, extracted from the App component so
// the component tree stays a thin composition of screens + routes. Owns both
// sign-in modes:
//
// - a normal tenant Supabase session (login, invite/reset recovery, refresh);
// - an admin "act as" session running on an API-minted, tenant-scoped act-as
//   token (no Supabase session at all — see ../lib/impersonation.ts). The
//   token's TTL outlasts a working day and is deliberately not refreshable:
//   when it expires, the expiry dialog sends the admin back to the Admin
//   Portal for a fresh session.
//
// The hook also owns the ApiClient: it pushes every new bearer token into the
// client's token store, which API calls read at request time. Token refreshes
// therefore never touch React state — pages depend on the stable `api`
// instance, so a refresh can't retrigger data-loading effects or wipe
// in-progress edits.
import { useCallback, useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { TenantConfig } from "../lib/config";
import { createTenantSupabaseClient } from "../lib/supabase";
import { readJwtClaims, hasTenantRoleClaim, readTenantIdClaim, describeRoleClaim, tokenExpiryMs } from "../lib/claims";
import { isRecoveryHash } from "@cms/portal-lib/recovery";
import {
  readImpersonationParams,
  readStoredImpersonation,
  storeImpersonation,
  clearStoredImpersonation,
  type Impersonation,
} from "../lib/impersonation";
import { createApiClient, type ApiClient } from "../lib/api";

export type PortalView =
  | "loading"
  | "config-error"
  | "login"
  | "set-password"
  | "forbidden"
  | "portal";

export interface AuthUser {
  id: string;
  email: string;
}

export interface ActiveImpersonation {
  label: string;
  returnTo: string;
}

export interface PortalAuth {
  view: PortalView;
  error: string;
  /** Authenticated API client; null when no API is configured. */
  api: ApiClient | null;
  user: AuthUser | null;
  tenantId: string;
  environment: string | null;
  impersonation: ActiveImpersonation | null;
  recoveryEmail: string;
  /** Bumped whenever an API call stays 401 after the silent refresh attempt. */
  authExpired: number;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  completeSetPassword: (password: string) => Promise<void>;
  /** Change the signed-in tenant's password; resolves to an error message, or null on success. */
  changePassword: (password: string) => Promise<string | null>;
}

interface MeResponse {
  environment?: string | null;
  actingAsTenant?: string;
}

export function usePortalAuth(config: TenantConfig): PortalAuth {
  const [view, setView] = useState<PortalView>("loading");
  const [error, setError] = useState("");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenantId, setTenantId] = useState("");
  const [environment, setEnvironment] = useState<string | null>(null);
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [impersonation, setImpersonation] = useState<ActiveImpersonation | null>(null);
  // Bumped whenever an API call stays 401 after the silent refresh attempt;
  // forces the act-as expiry modal open in its re-login state.
  const [authExpired, setAuthExpired] = useState(0);
  // One client for the app's lifetime — its identity never changes, so pages
  // and effects can depend on it safely.
  const [api] = useState<ApiClient | null>(() =>
    config.ok && config.apiBaseUrl ? createApiClient({ baseUrl: config.apiBaseUrl }) : null
  );
  // Invite/reset links land with recovery tokens in the hash — hold the user
  // on the set-password screen instead of entering the portal.
  const recoveryRef = useRef(isRecoveryHash(window.location.hash));
  // Admins arrive with act-as params in the hash; on later refreshes the hash is
  // gone, so fall back to the per-tab stored context to keep the session alive.
  const impersonationRef = useRef<Impersonation | null>(
    readImpersonationParams(window.location.hash) ?? readStoredImpersonation()
  );
  // Signed-in flag: Supabase re-emits auth events whenever the tab regains
  // focus (SIGNED_IN with the same token, or TOKEN_REFRESHED with a new one);
  // once signed in those only need the token pushed into the api client.
  const signedInRef = useRef(false);

  const resetSession = useCallback(() => {
    signedInRef.current = false;
    setUser(null);
    setTenantId("");
    setEnvironment(null);
    setImpersonation(null);
    setAuthExpired(0);
    api?.setToken("");
    api?.setActAsTenant(null);
    api?.setAuthExpiredListener(null);
    clearStoredImpersonation();
  }, [api]);

  const showForbidden = useCallback((message: string) => {
    resetSession();
    setError(message);
    setView("forbidden");
  }, [resetSession]);

  const acceptSession = useCallback(async (newSession: { access_token: string }) => {
    // Already signed in (tab-focus SIGNED_IN re-emits, or a TOKEN_REFRESHED
    // after the browser throttled a background tab): push the token into the
    // api client — nothing else changes, so no React state is touched and no
    // page effect re-runs.
    if (signedInRef.current) {
      api?.setToken(newSession.access_token);
      return;
    }

    let claims;
    try {
      claims = readJwtClaims(newSession.access_token);
    } catch (err) {
      showForbidden(`Unable to read JWT claims: ${err instanceof Error ? err.message : err}`);
      return;
    }

    // Admins belong in the admin console — only tenant accounts proceed.
    if (!hasTenantRoleClaim(claims)) {
      showForbidden(`This portal is for client accounts. Your role is "${describeRoleClaim(claims)}", not "tenant".`);
      return;
    }

    const claimTenantId = readTenantIdClaim(claims);
    if (!claimTenantId) {
      showForbidden("Your account is missing a tenant_id claim. Contact support to get this fixed.");
      return;
    }

    const newUser: AuthUser = {
      id: typeof claims.sub === "string" ? claims.sub : "",
      email: typeof claims.email === "string" ? claims.email : "",
    };

    if (api) {
      api.setToken(newSession.access_token);
      try {
        const me = await api.request<MeResponse>("/tenant/me");
        setEnvironment(me?.environment ?? null);
      } catch (err) {
        showForbidden(`API verification failed: ${err instanceof Error ? err.message : err}`);
        return;
      }
    }

    signedInRef.current = true;
    setUser(newUser);
    setTenantId(claimTenantId);
    setView("portal");
  }, [api, showForbidden]);

  // Admin "act as" entry: run the whole session on the handed-over act-as
  // token. The API derives the tenant from the token's verified claim (the
  // x-act-as-tenant header we still send may only confirm it); we never
  // establish a Supabase session in this mode.
  const acceptImpersonation = useCallback(async (imp: Impersonation) => {
    if (!api) {
      showForbidden("Impersonation requires the API to be configured.");
      return;
    }

    api.setActAsTenant(imp.tenantId);
    api.setToken(imp.token);

    // Any request that stays 401 (the token expired, or the server rejected
    // it) forces the expiry dialog open — the server's verdict, independent of
    // throttled timers or clock skew.
    api.setAuthExpiredListener(() => setAuthExpired((count) => count + 1));

    try {
      const me = await api.request<MeResponse>("/tenant/me");
      if (!me?.actingAsTenant) {
        showForbidden("The API did not accept this admin act-as session.");
        return;
      }
      setEnvironment(me?.environment ?? null);
    } catch (err) {
      // A tab that resumes with an expired stored token still enters the
      // portal: the expiry dialog (forced open via the listener above) points
      // back to the Admin Portal. Anything else is a genuine rejection.
      const expiry = tokenExpiryMs(api.getToken());
      const message = err instanceof Error ? err.message : String(err);
      const tokenExpired = (expiry !== null && expiry <= Date.now()) || /expired/i.test(message);
      if (!tokenExpired) {
        showForbidden(`Admin act-as verification failed: ${message}`);
        return;
      }
      setEnvironment(null);
    }

    signedInRef.current = true;
    setUser({ id: "", email: imp.email });
    setTenantId(imp.tenantId);
    setImpersonation({ label: imp.label, returnTo: imp.returnTo });
    // Persist per-tab so a refresh resumes the session, then strip the
    // handed-over token from the address bar / history once consumed.
    storeImpersonation(imp);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);

    setView("portal");
  }, [api, showForbidden]);

  useEffect(() => {
    if (!config.ok) {
      setError(config.message);
      setView("config-error");
      return;
    }

    // Impersonation runs entirely on the admin token — bypass Supabase auth.
    if (impersonationRef.current) {
      acceptImpersonation(impersonationRef.current);
      return;
    }

    const client = createTenantSupabaseClient({
      supabaseUrl: config.supabaseUrl,
      publishableKey: config.publishableKey,
    });
    setSupabase(client);

    // Save-resilience for normal logins: if an API call 401s (e.g. the token
    // lapsed while the tab was backgrounded, before autoRefresh caught up),
    // refresh the Supabase session and hand back the fresh token for a retry.
    // The api client adopts the returned token itself.
    api?.setAuthRefreshHandler(async () => {
      const { data, error: refreshError } = await client.auth.refreshSession();
      if (refreshError || !data.session) return null;
      return data.session.access_token;
    });

    const showSetPassword = (recoverySession: { user?: { email?: string } | null }) => {
      recoveryRef.current = true;
      setRecoveryEmail(recoverySession.user?.email ?? "");
      setError("");
      setView("set-password");
    };

    (async () => {
      const { data, error: sessionError } = await client.auth.getSession();
      if (sessionError) {
        setError(sessionError.message);
        setView("login");
        return;
      }
      if (data.session) {
        if (recoveryRef.current) {
          showSetPassword(data.session);
        } else {
          await acceptSession(data.session);
        }
      } else {
        recoveryRef.current = false;
        setView("login");
      }
    })();

    const { data: { subscription } } = client.auth.onAuthStateChange(async (event, newSession) => {
      if (event === "PASSWORD_RECOVERY" && newSession) {
        showSetPassword(newSession);
        return;
      }
      // While the set-password screen is up, ignore the SIGNED_IN/USER_UPDATED
      // events Supabase fires around it — completeSetPassword moves us on.
      if (recoveryRef.current) return;
      if (newSession) {
        await acceptSession(newSession);
      } else {
        resetSession();
        setView("login");
      }
    });

    return () => subscription.unsubscribe();
    // Bootstrap runs once; config is a module-scope constant in the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) return;
    setError("");
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError.message);
    }
  }, [supabase]);

  const completeSetPassword = useCallback(async (password: string) => {
    if (!supabase) return;
    setError("");
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      return;
    }
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    recoveryRef.current = false;
    setRecoveryEmail("");
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      await acceptSession(data.session);
    } else {
      setView("login");
    }
  }, [supabase, acceptSession]);

  const signOut = useCallback(async () => {
    // Impersonation has no Supabase session — close the tab the Admin Portal
    // opened. window.close() only works for script-opened tabs, so fall back to
    // navigating back to the Admin Portal (or the login screen) if it's a no-op.
    if (impersonation) {
      const returnTo = impersonation.returnTo;
      resetSession();
      window.close();
      if (returnTo) {
        window.location.href = returnTo;
      } else {
        setView("login");
      }
      return;
    }
    if (supabase) await supabase.auth.signOut();
    resetSession();
    setView("login");
  }, [impersonation, supabase, resetSession]);

  const changePassword = useCallback(async (password: string): Promise<string | null> => {
    // Admin act-as runs on a non-refreshable API token with no Supabase session —
    // it can't (and mustn't) change the tenant's password.
    if (impersonation) return "You can't change the password while managing this site as an admin.";
    if (!supabase) return "Authentication is not configured.";
    const { error: updateError } = await supabase.auth.updateUser({ password });
    return updateError ? updateError.message : null;
  }, [impersonation, supabase]);

  return {
    view,
    error,
    api,
    user,
    tenantId,
    environment,
    impersonation,
    recoveryEmail,
    authExpired,
    signIn,
    signOut,
    completeSetPassword,
    changePassword,
  };
}
